/** Post-Launch Analysis page (Phase 33) — operator-pasted only. */
import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  runPostLaunchAnalysisDryRun,
  type ApiResult,
  type DryRunChannel,
  type DryRunLanguage,
  type PostLaunchAnalysisRequest,
  type PostLaunchAnalysisResponse,
  runWorkflowPersistedFirst,
} from "../api/client";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import { FieldGrid, SelectField, TextAreaField, TextField } from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  sales_channel: string;
  review_snippets: string;
  return_reasons: string;
  customer_feedback_notes: string;
  ecommerce_performance_notes: string;
  packaging_version: string;
  notes: string;
  target_language: DryRunLanguage;
}

const EMPTY: FormState = {
  product_name: "",
  sales_channel: "",
  review_snippets: "",
  return_reasons: "",
  customer_feedback_notes: "",
  ecommerce_performance_notes: "",
  packaging_version: "",
  notes: "",
  target_language: "en",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<PostLaunchAnalysisResponse> };

const splitLines = (s: string, max: number): string[] =>
  s.split("\n").map((x) => x.trim()).filter(Boolean).slice(0, max);

export function PostLaunchAnalysisPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage) ? locale : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.product_name.trim()) errs.product_name = t.workflowDryRunRequiredHint;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const body: PostLaunchAnalysisRequest = {
      product_name: form.product_name.trim(),
      sales_channel: form.sales_channel ? (form.sales_channel as DryRunChannel) : null,
      review_snippets: splitLines(form.review_snippets, 64),
      return_reasons: splitLines(form.return_reasons, 64),
      customer_feedback_notes: splitLines(form.customer_feedback_notes, 64),
      ecommerce_performance_notes: form.ecommerce_performance_notes.trim() || null,
      packaging_version: form.packaging_version.trim() || null,
      notes: form.notes.trim() || null,
      target_language: form.target_language,
    };
    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("post-launch-analysis", body, runPostLaunchAnalysisDryRun);
    setState({ kind: "loaded", result });
  }

  const opt = (vals: readonly string[]) => vals.map((v) => ({ value: v, label: v }));

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">{t.appShellNavWorkflows}</span>
        <h1 className="po-section-header__title">{t.postLaunchTitle}</h1>
        <p className="po-note">{t.postLaunchSubtitle}</p>
      </header>
      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField label={t.intakeFieldProductName} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name} onChange={(v) => update("product_name", v)} error={errors.product_name} maxLength={200} />
            <TextField label={t.postLaunchFieldVersion} value={form.packaging_version} onChange={(v) => update("packaging_version", v)} maxLength={64} />
            <SelectField label={t.labelCopyFieldSalesChannel} value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)} options={opt(DRY_RUN_CHANNELS)} emptyLabel={t.workflowDryRunNotSelected} />
            <SelectField label={t.labelCopyFieldTargetLanguage} value={form.target_language}
              onChange={(v) => update("target_language", v as DryRunLanguage)} options={opt(DRY_RUN_LANGUAGES)} />
            <TextAreaField label={t.postLaunchFieldReviews} value={form.review_snippets} onChange={(v) => update("review_snippets", v)} rows={4} full />
            <TextAreaField label={t.postLaunchFieldReturns} value={form.return_reasons} onChange={(v) => update("return_reasons", v)} rows={3} full />
            <TextAreaField label={t.postLaunchFieldFeedback} value={form.customer_feedback_notes} onChange={(v) => update("customer_feedback_notes", v)} rows={3} full />
            <TextAreaField label={t.postLaunchFieldPerformance} value={form.ecommerce_performance_notes} onChange={(v) => update("ecommerce_performance_notes", v)} rows={2} maxLength={1000} full />
            <TextAreaField label={t.intakeFieldNotes} value={form.notes} onChange={(v) => update("notes", v)} rows={2} maxLength={1000} full />
          </FieldGrid>
          {Object.keys(errors).length > 0 && <p className="po-field__error">{t.workflowDryRunValidationFailed}</p>}
          <div className="po-row">
            <Button variant="primary" type="submit" disabled={state.kind === "submitting"}>
              {state.kind === "submitting" ? t.postLaunchSubmitting : t.postLaunchSubmit}
            </Button>
            <Button variant="secondary" type="button" onClick={() => { setForm({ ...EMPTY, target_language: form.target_language }); setErrors({}); setState({ kind: "idle" }); }}>
              {t.postLaunchReset}
            </Button>
          </div>
        </form>
      </section>
      {state.kind === "loaded" && (
        <DataState state={{ kind: "loaded", result: state.result }} t={t}
          renderOk={(data, requestId) => <PostLaunchReport data={data} requestId={requestId} t={t} />} />
      )}
    </AppLayout>
  );
}

function PostLaunchReport({ data, requestId, t }: { data: PostLaunchAnalysisResponse; requestId: string | null; t: UiStrings; }): JSX.Element {
  return (
    <div className="po-stack" data-testid="po-post-launch-report">
      <div className="po-halt-banner po-halt-banner--medium" role="alert">{data.no_external_notice}</div>
      <ReportSection title={t.postLaunchReportThemes}><BulletList items={data.feedback_theme_summary} /></ReportSection>
      <ReportSection title={t.postLaunchReportComplaints}><BulletList items={data.packaging_complaint_classification} empty={t.reportNoFlags} /></ReportSection>
      <ReportSection title={t.postLaunchReportReturns}><BulletList items={data.return_damage_classification} empty={t.reportNoFlags} /></ReportSection>
      <ReportSection title={t.postLaunchReportSentiment}><BulletList items={data.review_sentiment_notes} /></ReportSection>
      <ReportSection title={t.postLaunchReportImprovements}><BulletList items={data.packaging_improvement_suggestions} empty={t.reportNoFlags} /></ReportSection>
      <ReportSection title={t.postLaunchReportNext}><BulletList items={data.next_iteration_priorities} empty={t.reportNoFlags} /></ReportSection>
      <ReportSection title={t.postLaunchReportDataQuality}><BulletList items={data.data_quality_notes} /></ReportSection>
      <ReportSection title={t.intakeReportHumanReview}><BulletList items={data.human_review_required_notes} /></ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
